/**
 * The Bedrock HTTP wire shapes, as the server writes them.
 *
 * These mirror `SuccessResponse<T>`, `SuccessResponse`, `ResponseMessage` and `ProblemError`, and
 * nothing behind them: `Result` itself is never serialized. Omission is part of the contract —
 * `field`, `parameters` and `messages` are absent rather than null when empty.
 */
/** The media type a Bedrock problem document is written under (RFC 9457). */
export const PROBLEM_CONTENT_TYPE = 'application/problem+json';
const MESSAGE_KINDS = ['success', 'info', 'warning'];
function isJsonObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isStringRecord(value) {
    return isJsonObject(value) && Object.values(value).every((entry) => typeof entry === 'string');
}
/**
 * Whether a parsed value is a well-formed message.
 *
 * An absent `parameters` is valid and an explicit null is not: null is what the contract
 * deliberately does not write.
 */
export function isApiMessage(value) {
    if (!isJsonObject(value)) {
        return false;
    }
    if (typeof value['kind'] !== 'string' || !MESSAGE_KINDS.includes(value['kind'])) {
        return false;
    }
    if (typeof value['code'] !== 'string') {
        return false;
    }
    return value['parameters'] === undefined || isStringRecord(value['parameters']);
}
export function isApiMessageArray(value) {
    return Array.isArray(value) && value.every(isApiMessage);
}
function isProblemErrorItem(value) {
    if (!isJsonObject(value)) {
        return false;
    }
    if (typeof value['code'] !== 'string') {
        return false;
    }
    if (value['field'] !== undefined && typeof value['field'] !== 'string') {
        return false;
    }
    return value['parameters'] === undefined || isStringRecord(value['parameters']);
}
/**
 * Whether a parsed body is structurally a Bedrock problem document.
 *
 * `code` is the load-bearing member: a framework `ProblemDetails` that never passed through the
 * Bedrock boundary does not carry one, and answering it as a Bedrock refusal would invent a code
 * the server never wrote.
 */
export function isBedrockProblem(value) {
    if (!isJsonObject(value)) {
        return false;
    }
    if (typeof value['code'] !== 'string') {
        return false;
    }
    const errors = value['errors'];
    if (errors !== undefined && !(Array.isArray(errors) && errors.every(isProblemErrorItem))) {
        return false;
    }
    return value['messages'] === undefined || isApiMessageArray(value['messages']);
}
/**
 * Narrows a parsed problem body to the declared shape, defaulting the members the server omits.
 *
 * `errors` becomes an empty array rather than staying absent, so a caller can iterate without
 * first asking whether the member exists.
 */
export function toBedrockProblem(value, status) {
    const errors = Array.isArray(value['errors']) ? value['errors'] : [];
    return {
        status: typeof value['status'] === 'number' ? value['status'] : status,
        ...(typeof value['title'] === 'string' ? { title: value['title'] } : {}),
        ...(typeof value['type'] === 'string' ? { type: value['type'] } : {}),
        code: value['code'],
        errors,
        ...(isApiMessageArray(value['messages']) ? { messages: value['messages'] } : {}),
        ...(typeof value['traceId'] === 'string' ? { traceId: value['traceId'] } : {}),
        ...(typeof value['operationId'] === 'string' ? { operationId: value['operationId'] } : {}),
        ...(typeof value['rootOperationId'] === 'string'
            ? { rootOperationId: value['rootOperationId'] }
            : {}),
    };
}
//# sourceMappingURL=contracts.js.map